/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import { type ReactNode, useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { request } from '@mairistem/tools';
import { ModalEntites } from '@mairistem/entites';
import { Dimmer, Loader } from '@mairistem/composants';

import type { ApiListResponse } from '@mairistem/administres-utils';

import { useEntiteStore, useProduitDomaineStore } from '@/zustand';
import type { Entite } from '@/types';

interface EntiteZustandProviderProps {
  children: ReactNode;
}

const EntiteZustandProvider = (props: EntiteZustandProviderProps) => {
  const { children } = props;

  const selectedEntite = useEntiteStore((state) => state.selectedEntite);
  const selectEntite = useEntiteStore((state) => state.selectEntite);
  const setBlocked = useEntiteStore((state) => state.setBlocked);
  const refresh = useEntiteStore((state) => state.refresh);
  const setRefresh = useEntiteStore((state) => state.setRefresh);
  const produitDomaine = useProduitDomaineStore(
    (state) => state.produitDomaine,
  );

  const history = useHistory();

  const [openModal, setOpenModal] = useState(false);

  const handleSelect = useCallback(
    (entite: Entite) => {
      setRefresh(false);
      selectEntite(entite);
    },
    [selectEntite, setRefresh],
  );

  const handleClose = useCallback(() => {
    setRefresh(false);
    setOpenModal(false);
    history.goBack();
  }, [history, setRefresh]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: requis
  useEffect(() => {
    if (_.isNil(selectedEntite) && !_.isNil(produitDomaine)) {
      request
        .get<unknown, ApiListResponse<Entite>>(
          '/api/administres/etat-civil/entites',
        )
        .then(({ data }) => {
          if (data.length === 1) {
            selectEntite(data[0]);
          } else {
            setOpenModal(true);
          }
        });
    }
  }, [produitDomaine]);

  useEffect(() => {
    if (_.isNil(selectedEntite)) {
      setBlocked(true);
    } else {
      setOpenModal(false);
      setBlocked(false);
    }
  }, [selectedEntite, setBlocked]);

  useEffect(() => {
    if (refresh) {
      setOpenModal(true);
    }
  }, [refresh]);

  // Ajoute le header X-Entite aux requêtes
  // biome-ignore lint/correctness/useExhaustiveDependencies: requis
  useEffect(() => {
    const xEntite = selectedEntite?.identifiant;

    const interceptor = request.interceptors.request.use((config) => {
      config.headers['X-Entite'] = xEntite;

      return config;
    });

    return () => {
      request.interceptors.request.eject(interceptor);
    };
  }, [selectedEntite, produitDomaine]);
  return (
    <>
      {children}
      {!_.isNil(produitDomaine) && (
        <ModalEntites
          open={openModal}
          entiteSelected={selectedEntite}
          onSelect={handleSelect}
          onSave={() => null}
          onOpen={() => setOpenModal(true)}
          service="/api/administres/etat-civil/entites"
          // onClose={handleClose}
          {...(refresh && { onClose: handleClose })}
        />
      )}
      {_.isNil(selectedEntite) && (
        <Dimmer active inverted>
          <Loader size="large" />
        </Dimmer>
      )}
    </>
  );
};

export default EntiteZustandProvider;
